import React, {useCallback, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useGate, useStore} from 'effector-react';
import {useDropzone} from 'react-dropzone';
import {Controller, FieldError as HookFormFieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {ReactComponent as TrashIcon} from 'assets/svg/trash-2.svg';
import {ReactComponent as UploadIcon} from 'assets/svg/upload.svg';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {Textarea} from 'components/ui-kit/Textarea';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {createRequested, createState$, Gate} from 'models/complaints/create';
import {NkAppealCreateRequest} from 'types';

import {
    Actions,
    Dropzone,
    DropzoneAction,
    DropzoneContent,
    DropzoneHint,
    DropzoneText,
    FieldError,
    FileDeleteButton,
    FileName,
    FileTable,
    FileTableCell,
    FileTableHeadCell,
    FileTableInner,
    FilesField,
    FilesLabel,
    Form,
    FormGrid,
    FormRow,
    FullWidthField,
    RequiredMark,
    SubmitButton,
} from './styled';

const defaultValues: NkAppealCreateRequest = {
    theme: '',
    description: '',
    response: '',
    storeName: '',
    storeAddress: '',
    productName: '',
    markingCode: '',
    files: [],
};

interface ComplaintPhotosFieldProps {
    value: File[];
    onChange: (files: File[]) => void;
}

const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

const formatFileSize = (bytes: number, locale: string) => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return `0 ${FILE_SIZE_UNITS[0]}`;
    }

    if (bytes < 1024) {
        return `${bytes} ${FILE_SIZE_UNITS[0]}`;
    }

    const unitIndex = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        FILE_SIZE_UNITS.length - 1,
    );
    const value = bytes / 1024 ** unitIndex;

    return `${new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
    }).format(value)} ${FILE_SIZE_UNITS[unitIndex]}`;
};

const ComplaintPhotosField = ({value, onChange}: ComplaintPhotosFieldProps) => {
    const {i18n, t} = useTranslation();
    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const nextFiles = [...value, ...acceptedFiles]
                .filter(
                    (file, index, allFiles) =>
                        allFiles.findIndex(
                            (candidate) =>
                                candidate.name === file.name &&
                                candidate.size === file.size &&
                                candidate.lastModified === file.lastModified,
                        ) === index,
                )
                .slice(0, 5);

            onChange(nextFiles);
        },
        [onChange, value],
    );
    const {getInputProps, getRootProps, isDragActive} = useDropzone({
        accept: 'image/*',
        multiple: true,
        onDrop,
    });

    return (
        <>
            <Dropzone isDragActive={isDragActive} {...getRootProps()}>
                <input {...getInputProps()} />
                <DropzoneContent>
                    <Icon
                        source={UploadIcon}
                        width={30}
                        height={30}
                        fillColor={'transparent'}
                        strokeColor={'#90A1B9'}
                    />
                    <DropzoneText>
                        {isDragActive ? (
                            t('pages.complaints.create.dropHere')
                        ) : (
                            <>
                                {t('pages.complaints.create.dragAndDrop')}{' '}
                                <DropzoneAction>
                                    {t('pages.complaints.create.chooseFiles')}
                                </DropzoneAction>
                            </>
                        )}
                    </DropzoneText>
                    <DropzoneHint>{t('pages.complaints.create.photoHint')}</DropzoneHint>
                </DropzoneContent>
            </Dropzone>
            {value.length > 0 && (
                <FileTable>
                    <FileTableInner>
                        <thead>
                            <tr>
                                <FileTableHeadCell>
                                    {t('pages.complaints.create.fileName')}
                                </FileTableHeadCell>
                                <FileTableHeadCell>
                                    {t('pages.complaints.create.fileSize')}
                                </FileTableHeadCell>
                                <FileTableHeadCell>
                                    {t('pages.complaints.create.fileActions')}
                                </FileTableHeadCell>
                            </tr>
                        </thead>
                        <tbody>
                            {value.map((file) => (
                                <tr key={`${file.name}-${file.lastModified}`}>
                                    <FileTableCell>
                                        <FileName title={file.name}>{file.name}</FileName>
                                    </FileTableCell>
                                    <FileTableCell>
                                        {formatFileSize(file.size, i18n.language)}
                                    </FileTableCell>
                                    <FileTableCell>
                                        <FileDeleteButton
                                            tooltipId={`file-delete-${file.name}`}
                                            icon={
                                                <Icon
                                                    source={TrashIcon}
                                                    width={16}
                                                    height={16}
                                                    fillColor="transparent"
                                                    strokeColor="#62748E"
                                                />
                                            }
                                            onClick={() =>
                                                onChange(value.filter((item) => item !== file))
                                            }
                                        />
                                    </FileTableCell>
                                </tr>
                            ))}
                        </tbody>
                    </FileTableInner>
                </FileTable>
            )}
        </>
    );
};

export const ComplaintCreatePage = () => {
    const {t} = useTranslation();
    const state = useStore(createState$);
    useGate(Gate);
    const themeOptions = useMemo(
        () => state.themes.map((theme) => ({label: theme.name, value: theme.code})),
        [state.themes],
    );
    const schema = useMemo(
        () =>
            yup.object().shape({
                theme: yup
                    .string()
                    .trim()
                    .required(t('components.form.required')),
                description: yup
                    .string()
                    .trim()
                    .required(t('components.form.required')),
                response: yup
                    .string()
                    .trim()
                    .required(t('components.form.required')),
                files: yup
                    .array()
                    .min(1, t('pages.complaints.create.photoRequired'))
                    .required(t('pages.complaints.create.photoRequired')),
            }),
        [t],
    );
    const {control, errors, handleSubmit} = useForm<NkAppealCreateRequest>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(schema),
    });
    const errorText = (error?: HookFormFieldError) => error?.message;

    return (
        <>
            <BackTo to={ROUTE.COMPLAINTS}>{t('pages.complaints.detail.back')}</BackTo>
            <Page title={t('pages.complaints.create.title')}>
                <ContentContainer className="half-width-content-container">
                    <Form onSubmit={handleSubmit<NkAppealCreateRequest>(createRequested)}>
                        <FormGrid>
                            <Controller
                                name="theme"
                                control={control}
                                render={({value, onChange, onBlur}, {invalid}) => (
                                    <Select
                                        label={t('pages.complaints.fields.theme')}
                                        isRequired
                                        value={themeOptions.find(
                                            (option) => option.value === value,
                                        )}
                                        options={themeOptions}
                                        isLoading={state.isThemesLoading}
                                        hasError={invalid}
                                        onBlur={onBlur}
                                        onChange={(option: SelectOption<string> | null) =>
                                            onChange(option?.value ?? '')
                                        }
                                    />
                                )}
                            />
                            <Controller
                                name="markingCode"
                                control={control}
                                render={(props) => (
                                    <Input
                                        {...props}
                                        label={t('pages.complaints.fields.markingCode')}
                                        placeholder={t(
                                            'pages.complaints.fields.placeholders.markingCode',
                                        )}
                                    />
                                )}
                            />
                            <Controller
                                name="storeName"
                                control={control}
                                render={(props) => (
                                    <Input
                                        {...props}
                                        label={t('pages.complaints.detail.store')}
                                        placeholder={t(
                                            'pages.complaints.fields.placeholders.storeName',
                                        )}
                                    />
                                )}
                            />
                            <FormRow>
                                <Controller
                                    name="storeAddress"
                                    control={control}
                                    render={(props) => (
                                        <Input
                                            {...props}
                                            label={t('pages.complaints.detail.address')}
                                            placeholder={t(
                                                'pages.complaints.fields.placeholders.storeAddress',
                                            )}
                                        />
                                    )}
                                />
                                <Controller
                                    name="productName"
                                    control={control}
                                    render={(props) => (
                                        <Input
                                            {...props}
                                            label={t('pages.complaints.detail.product')}
                                            placeholder={t(
                                                'pages.complaints.fields.placeholders.productName',
                                            )}
                                        />
                                    )}
                                />
                            </FormRow>
                            <FullWidthField>
                                <Controller
                                    name="description"
                                    control={control}
                                    render={(props) => (
                                        <Textarea
                                            style={{padding: '16px'}}
                                            {...props}
                                            label={t('pages.complaints.detail.description')}
                                            isRequired
                                            hasError={Boolean(errors.description)}
                                            placeholder={t(
                                                'pages.complaints.fields.placeholders.description',
                                            )}
                                        />
                                    )}
                                />
                                {errors.description && (
                                    <FieldError>{errorText(errors.description)}</FieldError>
                                )}
                            </FullWidthField>
                            <FullWidthField>
                                <Controller
                                    name="response"
                                    control={control}
                                    render={(props) => (
                                        <Textarea
                                            style={{padding: '16px'}}
                                            {...props}
                                            label={t('pages.complaints.detail.response')}
                                            isRequired
                                            hasError={Boolean(errors.response)}
                                            placeholder={t(
                                                'pages.complaints.fields.placeholders.response',
                                            )}
                                        />
                                    )}
                                />
                                {errors.response && (
                                    <FieldError>{errorText(errors.response)}</FieldError>
                                )}
                            </FullWidthField>
                        </FormGrid>
                        <FilesField>
                            {/* <FilesLabel>
                                {t('pages.complaints.create.photos')} <RequiredMark>*</RequiredMark>
                            </FilesLabel> */}
                            <Controller
                                name="files"
                                control={control}
                                render={({value = [], onChange}) => (
                                    <ComplaintPhotosField value={value} onChange={onChange} />
                                )}
                            />
                            <FieldError>
                                {errorText((errors.files as unknown) as HookFormFieldError)}
                            </FieldError>
                        </FilesField>
                        <Actions>
                            <Button
                                type="button"
                                size="md"
                                variant={ButtonVariant.Outline}
                                onClick={() => history.push(ROUTE.COMPLAINTS)}
                            >
                                {t('components.form.Cancel')}
                            </Button>
                            <SubmitButton type="submit" size="md" isLoading={state.isSubmitting}>
                                {t('pages.complaints.create.submit')}
                            </SubmitButton>
                        </Actions>
                    </Form>
                    <div />
                </ContentContainer>
            </Page>
        </>
    );
};
